import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface VolumeLoadChartProps {
  data: Record<string, unknown>;
}

export function VolumeLoadChart({ data }: VolumeLoadChartProps) {
  const chartData = useMemo(() => {
    const volumeTrend = data.volume_load_trend as Array<{ date: string; value: number }> | undefined;
    if (!volumeTrend?.length) return [];
    return volumeTrend.map((p) => ({
      date: p.date,
      volume: Math.round(p.value),
    }));
  }, [data]);

  if (!chartData.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Volume Load Trend</h3>
        <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
          No volume load data yet. Log exercises to track weekly volume.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">Volume Load Trend</h3>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(0 70% 55%)" stopOpacity={0.5} />
                <stop offset="95%" stopColor="hsl(0 70% 55%)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={50} />
            <Tooltip
              contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }}
              formatter={(value: number) => [`${value.toLocaleString()} kg`, "Volume"]}
            />
            <Bar dataKey="volume" fill="url(#volGrad)" radius={[4, 4, 0, 0]} maxBarSize={24} name="Volume Load" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
